import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { MessageSquareWarning } from 'lucide-react';

/**
 * SlipReasonsPanel — why projects slipped, week-over-week.
 * Data: GET /api/master-projects/health/slip-reasons?weeks=N
 * Reasons are user-entered whenever a milestone target is shifted.
 */
export function SlipReasonsPanel({ weeks = 8, testId = 'slip-reasons-panel' }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/master-projects/health/slip-reasons?weeks=${weeks}`)
      .then(r => setData(r.data))
      .catch(() => {});
  }, [weeks]);

  if (!data) return null;

  const header = (
    <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold flex items-center gap-2">
      <MessageSquareWarning size={12} />Why projects slipped · top root causes
    </div>
  );

  if (!data.total) {
    return (
      <div className="card p-4 elev-1" data-testid={testId}>
        {header}
        <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-2">
          No slip reasons recorded yet. Whenever someone shifts a milestone target,
          they pick <i>why</i> — those answers show up here week-over-week.
        </div>
      </div>
    );
  }

  const maxCount = Math.max(1, ...data.overall_top.map(t => t.count));
  const recentWeeks = [...(data.weeks || [])].reverse();

  return (
    <div className="card p-4 elev-1" data-testid={testId}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        {header}
        <span className="text-[11px] text-[hsl(var(--foreground-subtle))] tabular-nums">
          {data.total} shift{data.total === 1 ? '' : 's'} in the last {weeks} weeks
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
        <div data-testid={`${testId}-overall`}>
          <div className="text-[12px] font-medium mb-2">Top 3 overall</div>
          <div className="space-y-2">
            {data.overall_top.map((t, i) => (
              <div key={t.reason} data-testid={`${testId}-overall-${t.reason}`}>
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="truncate">
                    <span className="text-[hsl(var(--foreground-subtle))] font-mono mr-1.5">#{i + 1}</span>
                    {t.label}
                  </span>
                  <span className="tabular-nums font-medium ml-2">{t.count}</span>
                </div>
                <div className="h-1.5 rounded-full mt-1" style={{ background: 'hsl(var(--surface-2))' }}>
                  <div className="h-1.5 rounded-full"
                       style={{ width: `${(t.count / maxCount) * 100}%`, background: 'hsl(var(--warning))' }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div data-testid={`${testId}-weekly`}>
          <div className="text-[12px] font-medium mb-2">Week-over-week</div>
          <div className="space-y-1.5">
            {recentWeeks.map(w => (
              <div key={w.week} className="flex items-start gap-2 text-[11.5px]" data-testid={`${testId}-week-${w.week}`}>
                <span className="font-mono text-[hsl(var(--foreground-subtle))] flex-none w-[70px] tabular-nums">
                  {w.week_start?.slice(5)}
                </span>
                <div className="flex flex-wrap gap-1 min-w-0">
                  {w.top.map(t => (
                    <span key={t.reason} className="chip chip-warning" style={{ fontSize: '10.5px' }}>
                      {t.label} ×{t.count}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
